import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminRegionsService } from "@/services/admin-regions-service";
import {
  RegionBootstrapResultDto,
  RegionResponseDto,
  RegionsFilters,
} from "@/types/admin-region";
import { adminStorageKeys } from "@/hooks/useAdminStorage";

const FIVE_MINUTES = 5 * 60 * 1000;

export const adminRegionKeys = {
  all: ["admin", "regions"] as const,
  lists: () => [...adminRegionKeys.all, "list"] as const,
  list: (filters?: RegionsFilters) =>
    [...adminRegionKeys.lists(), { filters }] as const,
  details: () => [...adminRegionKeys.all, "detail"] as const,
  detail: (code: string) => [...adminRegionKeys.details(), code] as const,
};

export const useAdminRegions = (filters?: RegionsFilters) => {
  return useQuery<RegionResponseDto[]>({
    queryKey: adminRegionKeys.list(filters),
    queryFn: () => adminRegionsService.getRegions(filters),
    staleTime: FIVE_MINUTES,
  });
};

export const useAdminRegion = (code: string | undefined) => {
  return useQuery<RegionResponseDto>({
    queryKey: adminRegionKeys.detail(code || ""),
    queryFn: () => adminRegionsService.getRegion(code as string),
    enabled: !!code,
    staleTime: FIVE_MINUTES,
  });
};

export const useBootstrapRegions = () => {
  const queryClient = useQueryClient();
  return useMutation<RegionBootstrapResultDto, Error, void>({
    mutationFn: () => adminRegionsService.bootstrapRegions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminRegionKeys.all });
      queryClient.invalidateQueries({ queryKey: adminStorageKeys.all });
    },
  });
};
